import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ActiveExport } from 'types/exports';

export interface ExportsState {
    activeExports: Record<string, ActiveExport>;
    isRibbonVisible: boolean;
    isRibbonMinimized: boolean;
}

const initialState: ExportsState = {
    activeExports: {},
    isRibbonVisible: false,
    isRibbonMinimized: false,
};

const slice = createSlice({
    name: 'exports',
    initialState,
    reducers: {
        addExport: (state, action: PayloadAction<ActiveExport>) => {
            state.activeExports[action.payload.taskId] = action.payload;
            state.isRibbonVisible = true;
        },
        updateExport: (state, action: PayloadAction<Partial<ActiveExport> & { taskId: string }>) => {
            const existing = state.activeExports[action.payload.taskId];
            if (existing) {
                state.activeExports[action.payload.taskId] = {
                    ...existing,
                    ...action.payload,
                };
            }
        },
        removeExport: (state, action: PayloadAction<string>) => {
            delete state.activeExports[action.payload];
            if (Object.keys(state.activeExports).length === 0) {
                state.isRibbonVisible = false;
            }
        },
        toggleRibbon: (state) => {
            state.isRibbonMinimized = !state.isRibbonMinimized;
        },
        hideRibbon: (state) => {
            state.isRibbonVisible = false;
        },
        showRibbon: (state) => {
            state.isRibbonVisible = true;
        },
        clearCompletedExports: (state) => {
            const activeExports: Record<string, ActiveExport> = {};
            Object.entries(state.activeExports).forEach(([taskId, exp]) => {
                if (exp.status !== 'COMPLETED' && exp.status !== 'FAILED') {
                    activeExports[taskId] = exp;
                }
            });
            state.activeExports = activeExports;
            if (Object.keys(state.activeExports).length === 0) {
                state.isRibbonVisible = false;
            }
        },
        setExports: (state, action: PayloadAction<ActiveExport[]>) => {
            const newExports: Record<string, ActiveExport> = {};
            action.payload.forEach((exp) => {
                newExports[exp.taskId] = exp;
            });
            state.activeExports = newExports;
            state.isRibbonVisible = action.payload.length > 0;
        },
    },
});

/** The export ribbon's own render condition: the flag alone can be set over an empty list. */
export const selectIsRibbonOnScreen = (state: { exports: ExportsState }) =>
    state.exports.isRibbonVisible && Object.keys(state.exports.activeExports).length > 0;

export default slice.reducer;
export const {
    addExport,
    updateExport,
    removeExport,
    toggleRibbon,
    hideRibbon,
    showRibbon,
    clearCompletedExports,
    setExports,
} = slice.actions;
